import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import { DataTable, Vehicle } from 'remote/global';
import { Box, Grid, Switch, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/utils/data/routes';

interface Props {
  vehicles: {
    elements: Vehicle[];
    totalElements: number;
    pageNo: number;
    pageSize: number;
    pageCount: number;
  };
  loading: boolean;
  onActivationChange?: (id: string, checked: boolean) => void;
  onPageChange?: (page: number) => void;
  currentPage: number;
  pageSize: number;
  totalElements: number;
}

function VehiclesTable({
  vehicles,
  loading,
  onActivationChange,
  onPageChange,
  currentPage,
  pageSize,
  totalElements,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'licensePlate',
        headerName: `${t('LogisticsView.rides.table.header.licensePlate')}`,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography
            sx={{ fontSize: '14px', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() =>
              navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET, row.id].join('/'))
            }
          >
            {row.licensePlate}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: `${t('LogisticsView.rides.table.header.status')}`,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: '14px' }}>{t(`BusStatus.${row.status}`)}</Typography>
        ),
      },
      {
        field: 'activation',
        headerName: `${t('terms.attributes.vehicle.activation')}`,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row }) => (
          <Box display='flex' justifyContent='center'>
            <Switch
              checked={row.availableForOffer}
              onChange={(e) => onActivationChange?.(row.id, e.target.checked)}
              size='small'
            />
          </Box>
        ),
      },
      {
        field: 'internationalDrives',
        headerName: `${t('FormFields.acceptInternationalRides.label')}`,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (row.acceptInternationalRides ? 'Yes' : 'No'),
      },
      {
        field: 'make',
        headerName: `${t('OffersView.table.header.vehicleMake')}`,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'year',
        headerName: `${t('FleetView.table.header.year')}`,
        flex: 1,
        minWidth: 80,
      },

      {
        field: 'seats',
        headerName: `${t('terms.attributes.vehicle.seats')}`,
        flex: 1,
        minWidth: 80,
      },
      {
        field: 'location',
        headerName: `${t('terms.attributes.vehicle.location')}`,
        flex: 1,
        minWidth: 200,
        valueGetter: ({ row }) => row.defaultLocation.address || '',
      },
      {
        field: 'driver',
        headerName: `${t('LogisticsView.rides.table.header.driverName')}`,
        flex: 1,
        minWidth: 148,
        valueGetter: ({ row }) => row.currentDriverName || '',
      },
    ];
  }, [t, onActivationChange, navigate]);

  return (
    <Grid item sx={{ p: { xs: 1, md: 2 } }}>
      <DataTable
        rows={vehicles}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id.toString()}
        t={t}
        onPageChange={onPageChange}
        count={Math.ceil(totalElements / pageSize)}
        page={currentPage + 1}
      />
    </Grid>
  );
}

export default VehiclesTable;
